import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button, ConfigProvider, Form, Input, Modal, Radio, Select, Tag } from "antd";
import {
  CloseOutlined,
  MailOutlined,
  PhoneOutlined,
  GoogleOutlined,
  LockOutlined,
  EyeTwoTone,
  EyeInvisibleOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  ROLES,
  isEmailBasedRole,
  isEitherContactRole,
  isShopStockManager,
} from "../../utils/roles";
import { getSession } from "../../utils/session";
import {
  fetchCategoriesAndSubcategories,
  CategoriesAndSubcategoriesState,
} from "../../store/categoriesAndSubcategories.slice";
import { AllOrderManagementState } from "../../store/allOrders.slice";
import "./InviteEmployeeModal.scss";

export interface InviteEmployeeFormData {
  role:
    | "SHOP_DELIVERY"
    | "SHOP_SUPERVISOR"
    | "SHOP_PICKER"
    | "SHOP_CATALOG_EDITOR"
    | "SHOP_STOCK_MANAGER";
  fullName: string;
  phoneNumber?: string;
  email?: string;
  contactMethod?: "email" | "phone";
  // Login type for email-capable roles. "Email" requires a password,
  // "Google" does not.
  loginType?: "Email" | "Google";
  password?: string;
  // For SHOP_STOCK_MANAGER: the allowed categories. On submit this is
  // normalised to ["*"] when all categories are selected.
  allowedCategories?: string[];
}

// Sentinel value used for the "All categories" option in the categories
// dropdown. It never leaves this component: on submit it is converted to ["*"].
const ALL_CATEGORIES_VALUE = "__ALL_CATEGORIES__";

// Details surfaced in the modal after a successful invite so the popup can
// show an "account created" confirmation instead of closing.
export interface InviteSuccessInfo {
  name: string;
  // The role the account was created as (drives the "as a <role>" copy).
  role: string;
  // The invite channel that determines the confirmation copy.
  method: "email" | "google" | "phone";
  email?: string;
  phoneNumber?: string;
}

interface InviteEmployeeModalProps {
  open: boolean;
  onCancel: () => void;
  // Returning InviteSuccessInfo keeps the modal open and shows the created
  // account confirmation. Returning void/undefined (e.g. on update or error)
  // preserves the previous close-on-submit behaviour.
  onSubmit: (
    values: InviteEmployeeFormData
  ) => void | InviteSuccessInfo | Promise<void | InviteSuccessInfo>;
  initialData?: InviteEmployeeFormData;
  title?: string;
  submitButtonText?: string;
  // When provided, the modal opens directly on the login-help confirmation
  // (display only, no invite form). Used by the table's login-type icon to
  // show the same "how to login" message for an existing employee.
  displaySuccessInfo?: InviteSuccessInfo | null;
}

const InviteEmployeeModal: React.FC<InviteEmployeeModalProps> = ({
  open,
  onCancel,
  onSubmit,
  initialData,
  title = "Invite New Employee",
  submitButtonText = "Invite Employee",
  displaySuccessInfo = null,
}) => {
  const [form] = Form.useForm<InviteEmployeeFormData>();
  const [isFormValid, setIsFormValid] = useState(false);
  // When set, the modal shows the "account created" confirmation instead of
  // the invite form. Cleared when the modal is closed/reopened.
  const [successInfo, setSuccessInfo] = useState<InviteSuccessInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUpdatingFieldsRef = useRef(false);
  const isInitializingRef = useRef(false);
  // Ensures allowed categories are prefilled only once per modal open so that
  // subsequent parent re-renders (which recreate initialData) don't clobber edits.
  const categoriesInitializedRef = useRef(false);

  const dispatch = useDispatch();

  const currentUserRole = getSession("role");
  const isShopAdminRole = currentUserRole === ROLES.SHOP_ADMIN;
  const isShopSupervisorRole = currentUserRole === "SHOP_SUPERVISOR";

  // Watch role field to determine if we should show phone or email
  const selectedRole = Form.useWatch("role", form);
  // Also check initialData role for conditional rendering when editing
  const effectiveRole = selectedRole || initialData?.role;
  // Watch contact method for SHOP_CATALOG_EDITOR
  const contactMethod = Form.useWatch("contactMethod", form);
  // Watch login type (Google / Email) for email-capable roles
  const loginType = Form.useWatch("loginType", form);
  // Watch allowed categories for the stock manager select-all behaviour
  const allowedCategories = Form.useWatch("allowedCategories", form) as
    | string[]
    | undefined;

  const shopId = useSelector(
    (state: { allOrderManagement: AllOrderManagementState }) =>
      state?.allOrderManagement?.shopId
  );
  const categoriesData = useSelector(
    (state: { categoriesAndSubcategories: CategoriesAndSubcategoriesState }) =>
      state?.categoriesAndSubcategories?.list?.data || null
  );
  const categoriesLoading = useSelector(
    (state: { categoriesAndSubcategories: CategoriesAndSubcategoriesState }) =>
      state?.categoriesAndSubcategories?.list?.loading || false
  );

  // Flatten the { category: [subcategory, ...] } response into a de-duped list
  // of the subcategory names that populate the dropdown.
  const allCategoryNames = useMemo(() => {
    if (!categoriesData) return [];
    const names = Object.values(categoriesData).flat();
    return Array.from(new Set(names));
  }, [categoriesData]);

  const isStockManagerRole = isShopStockManager(effectiveRole || "");

  // Whether the email input (and therefore the login-type selector) is shown.
  // True for pure email-based roles, and for the catalog editor when the user
  // has chosen to invite via email.
  const isEmailInputShown =
    isEmailBasedRole(effectiveRole) ||
    (isEitherContactRole(effectiveRole) && contactMethod === "email");

  // Password field is only shown/required when the Email login type is chosen.
  const isPasswordShown = isEmailInputShown && loginType === "Email";

  // Options for the categories dropdown. "All categories" is a convenience
  // option that fills in every individual category; the individual options
  // stay enabled so each resulting pill remains removable.
  const categoryOptions = useMemo(
    () => [
      { label: "All categories", value: ALL_CATEGORIES_VALUE },
      ...allCategoryNames.map((name) => ({
        label: name,
        value: name,
      })),
    ],
    [allCategoryNames]
  );

  // Normalises the raw dropdown change into the stored form value, driving the
  // "All categories" select-all / clear-all behaviour. The sentinel is kept in
  // the value only while every category is selected, so that removing a single
  // pill cleanly drops it back to a partial selection.
  const normalizeCategories = (selected: string[]): string[] => {
    const prev = allowedCategories || [];
    const hadAll = prev.includes(ALL_CATEGORIES_VALUE);
    const hasAll = selected.includes(ALL_CATEGORIES_VALUE);

    if (hasAll && !hadAll) {
      // Just picked "All categories" -> select every category.
      return [ALL_CATEGORIES_VALUE, ...allCategoryNames];
    }
    if (!hasAll && hadAll) {
      // Just removed the "All categories" option -> clear everything.
      return [];
    }

    // Otherwise work with the concrete category names only.
    const names = selected.filter((c) => c !== ALL_CATEGORIES_VALUE);
    const allNamesSelected =
      allCategoryNames.length > 0 && names.length === allCategoryNames.length;

    // Re-attach the sentinel only when the full set is selected, so submit can
    // still collapse it to ["*"].
    return allNamesSelected ? [ALL_CATEGORIES_VALUE, ...names] : names;
  };

  // Renders the selected categories as removable pills. The "All categories"
  // sentinel is only a convenience option that fills in every individual
  // category, so it is never drawn as a pill itself - each real category gets
  // its own removable pill.
  const renderCategoryTag = (props: {
    label: React.ReactNode;
    value: unknown;
    closable: boolean;
    onClose: (e?: React.MouseEvent<HTMLElement>) => void;
  }) => {
    const { label, value, closable, onClose } = props;

    // Never render the sentinel as a visible tag.
    if (value === ALL_CATEGORIES_VALUE) return <></>;

    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        className="invite-category-tag"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
      >
        {label}
      </Tag>
    );
  };

  // Validation rules - phone number required for delivery agents only
  const getPhoneNumberRules = (role: string | undefined) => {
    const rules: any[] = [];
    if (role === "SHOP_DELIVERY") {
      rules.push({ required: true, message: "Phone number is required" });
    }
    // Only validate pattern if value is provided
    rules.push({
      validator: (_: any, value: string) => {
        if (!value) {
          return Promise.resolve(); // Allow empty if not required
        }
        if (!/^\d{10,11}$/.test(value)) {
          return Promise.reject(new Error("Phone number must be 10-11 digits"));
        }
        return Promise.resolve();
      },
    });
    return rules;
  };

  // Validation rules - email required for supervisors only
  const getEmailRules = (role: string | undefined) => {
    const rules: any[] = [];
    if (isEmailBasedRole(role)) {
      rules.push({ required: true, message: "Email is required" });
    }
    // Only validate email format if value is provided
    rules.push({
      validator: (_: any, value: string) => {
        if (!value) {
          return Promise.resolve(); // Allow empty if not required
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return Promise.reject(
            new Error("Please enter a valid email address")
          );
        }
        return Promise.resolve();
      },
    });
    return rules;
  };

  // Validation rules - password required only when Email login type is chosen
  const getPasswordRules = (required: boolean) => {
    const rules: any[] = [];
    if (required) {
      rules.push({ required: true, message: "Password is required" });
      rules.push({
        min: 6,
        message: "Password must be at least 6 characters",
      });
    }
    return rules;
  };

  // Handler for numeric-only phone number input
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/\D/g, "").slice(0, 11);
    form.setFieldsValue({ phoneNumber: numericValue });
  };

  const fullNameRules = [
    { required: true, message: "Full name is required" },
    {
      min: 3,
      message: "Full name must be at least 3 characters",
    },
  ];

  const roleRules = [{ required: true, message: "Role is required" }];

  // Validation function
  const validateForm = useCallback(async () => {
    try {
      const values = form.getFieldsValue();
      const role = values?.role;

      // Check if role is required and provided
      if (isShopAdminRole && !role) {
        setIsFormValid(false);
        return;
      }

      // Check full name
      if (!values.fullName || values.fullName.length < 3) {
        setIsFormValid(false);
        return;
      }

      // Check contact field based on role
      const effectiveRole = role;

      if (isEitherContactRole(effectiveRole)) {
        // For catalog editor, either email or phone is required based on contactMethod
        const isEmail = (values.contactMethod || "phone") === "email";
        const value = isEmail ? values.email : values.phoneNumber;
        const regex = isEmail ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/ : /^\d{10,11}$/;

        if (!value || !regex.test(value)) {
          setIsFormValid(false);
          return;
        }
        // When inviting via email, a login type must be chosen and, if Email,
        // a password is required.
        if (isEmail) {
          if (!values.loginType) {
            setIsFormValid(false);
            return;
          }
          if (values.loginType === "Email") {
            if (!values.password || values.password.length < 6) {
              setIsFormValid(false);
              return;
            }
          }
        }
      } else if (isEmailBasedRole(effectiveRole)) {
        // Email is required for supervisors
        if (!values.email) {
          setIsFormValid(false);
          return;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(values.email)) {
          setIsFormValid(false);
          return;
        }
        // A login type must be chosen; Email requires a password.
        if (!values.loginType) {
          setIsFormValid(false);
          return;
        }
        if (values.loginType === "Email") {
          if (!values.password || values.password.length < 6) {
            setIsFormValid(false);
            return;
          }
        }
      } else {
        // Phone number is required for delivery agents
        if (!values.phoneNumber) {
          setIsFormValid(false);
          return;
        }
        // Validate phone format (10-11 digits)
        const phoneRegex = /^\d{10,11}$/;
        if (!phoneRegex.test(values.phoneNumber)) {
          setIsFormValid(false);
          return;
        }
      }

      // Stock managers must have at least one allowed category selected
      if (isShopStockManager(effectiveRole)) {
        if (!values.allowedCategories || values.allowedCategories.length === 0) {
          setIsFormValid(false);
          return;
        }
      }

      // Validate all fields with Ant Design validation
      await form.validateFields();
      setIsFormValid(true);
    } catch {
      setIsFormValid(false);
    }
  }, [form, isShopAdminRole, isShopSupervisorRole]);

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      setIsFormValid(false);
      // In display mode, open straight onto the confirmation. Otherwise start
      // on the form view and clear any prior success confirmation.
      setSuccessInfo(displaySuccessInfo ?? null);
      setIsSubmitting(false);
      // Clear any pending validations
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      form.resetFields();
      isInitializingRef.current = true;
      categoriesInitializedRef.current = false;
      if (initialData) {
        // Prefill form with initial data for editing
        // Set role first to ensure conditional fields are rendered
        form.setFieldsValue({ role: initialData.role });
      }
    } else {
      // Reset initialization flag when modal closes
      isInitializingRef.current = false;
      categoriesInitializedRef.current = false;
    }
  }, [open, form, isShopSupervisorRole, initialData, displaySuccessInfo]);

  // Set remaining form fields after role is set and conditional fields are rendered
  useEffect(() => {
    if (!open || !isInitializingRef.current) return;

    // Get the current role from form to check if it matches what we expect
    const currentRole = form.getFieldValue("role");
    const expectedRole = initialData?.role;

    // Only proceed if we have a role set from initialData
    if (!expectedRole || currentRole !== expectedRole) return;

    // Use double requestAnimationFrame to ensure DOM has fully updated
    // This ensures conditional fields (email/phone) are rendered before setting values
    let frameId2: number | undefined;
    const frameId1 = requestAnimationFrame(() => {
      frameId2 = requestAnimationFrame(() => {
        if (initialData) {
          // Set all other fields once the role is set and fields are rendered
          form.setFieldsValue({
            fullName: initialData.fullName,
            phoneNumber: initialData.phoneNumber,
            email: initialData.email,
            contactMethod: initialData.contactMethod,
            loginType: initialData.loginType,
            password: initialData.password,
          });
          validateForm();
        }
        isInitializingRef.current = false;
      });
    });

    return () => {
      cancelAnimationFrame(frameId1);
      if (frameId2 !== undefined) {
        cancelAnimationFrame(frameId2);
      }
    };
  }, [
    open,
    selectedRole,
    initialData,
    form,
    validateForm,
    isShopSupervisorRole,
  ]);

  // Fetch the shop's categories whenever the stock manager role is in play
  useEffect(() => {
    if (!open || !isStockManagerRole || !shopId) return;
    dispatch(fetchCategoriesAndSubcategories({ shopId }));
  }, [open, isStockManagerRole, shopId, dispatch]);

  // Prefill allowed categories when editing an existing stock manager. Runs once
  // per open; for the "*" (all) case it waits until the category list has loaded.
  useEffect(() => {
    if (!open || categoriesInitializedRef.current || !isStockManagerRole) return;

    const initialCats = initialData?.allowedCategories;
    if (!initialCats || initialCats.length === 0) return;

    if (initialCats.includes("*")) {
      // "All categories" was stored - expand once options are available
      if (allCategoryNames.length > 0) {
        form.setFieldsValue({
          allowedCategories: [ALL_CATEGORIES_VALUE, ...allCategoryNames],
        });
        categoriesInitializedRef.current = true;
        validateForm();
      }
    } else {
      form.setFieldsValue({ allowedCategories: initialCats });
      categoriesInitializedRef.current = true;
      validateForm();
    }
  }, [
    open,
    isStockManagerRole,
    initialData,
    allCategoryNames,
    form,
    validateForm,
  ]);

  // Update validation rules when role changes (only when user manually changes role, not during initialization)
  useEffect(() => {
    if (!open || isInitializingRef.current) return;

    isUpdatingFieldsRef.current = true;
    // Clear errors and clear field values when role changes
    if (isEitherContactRole(selectedRole)) {
      // Clear both fields and set default contact method to phone for catalog editor
      form.setFieldsValue({ phoneNumber: undefined, email: undefined, contactMethod: "phone" });
      form.setFields([
        { name: "phoneNumber", errors: [] },
        { name: "email", errors: [] },
        { name: "contactMethod", errors: [] },
      ]);
    } else if (isEmailBasedRole(selectedRole)) {
      // Clear phone number when switching to email-based roles (supervisor / picker)
      form.setFieldsValue({ phoneNumber: undefined, contactMethod: undefined });
      form.setFields([
        {
          name: "phoneNumber",
          errors: [],
        },
        {
          name: "email",
          errors: [],
        },
      ]);
    } else if (selectedRole === "SHOP_DELIVERY") {
      // Clear email when switching to phone-based roles (delivery only)
      form.setFieldsValue({ email: undefined, contactMethod: undefined });
      form.setFields([
        {
          name: "phoneNumber",
          errors: [],
        },
        {
          name: "email",
          errors: [],
        },
      ]);
    } else {
      // Clear both fields if no role selected
      form.setFieldsValue({ phoneNumber: undefined, email: undefined, contactMethod: undefined });
      form.setFields([
        {
          name: "phoneNumber",
          errors: [],
        },
        {
          name: "email",
          errors: [],
        },
      ]);
    }

    // Allowed categories only apply to stock managers - clear them otherwise
    if (!isShopStockManager(selectedRole || "")) {
      form.setFieldsValue({ allowedCategories: undefined });
      form.setFields([{ name: "allowedCategories", errors: [] }]);
    }

    // Reset flag and validate after form updates
    // Use requestAnimationFrame to ensure form state has updated
    const frameId = requestAnimationFrame(() => {
      isUpdatingFieldsRef.current = false;
      validateForm();
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [selectedRole, open, form, isShopSupervisorRole, validateForm]);

  // Debounced validation function
  const debouncedValidate = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    validationTimeoutRef.current = setTimeout(() => {
      validateForm();
    }, 300);
  }, [validateForm]);

  // Keep loginType / password in sync with whether the email input is shown.
  // Clears them when the email input goes away, and clears the password when a
  // non-Email login type is selected.
  useEffect(() => {
    if (!open || isInitializingRef.current) return;

    if (!isEmailInputShown) {
      const current = form.getFieldsValue(["loginType", "password"]);
      if (current.loginType !== undefined || current.password !== undefined) {
        isUpdatingFieldsRef.current = true;
        form.setFieldsValue({ loginType: undefined, password: undefined });
        form.setFields([
          { name: "loginType", errors: [] },
          { name: "password", errors: [] },
        ]);
        const frameId = requestAnimationFrame(() => {
          isUpdatingFieldsRef.current = false;
          validateForm();
        });
        return () => cancelAnimationFrame(frameId);
      }
      return;
    }

    // Email input is shown but a non-Email login type is selected: drop password
    if (loginType !== "Email") {
      const current = form.getFieldValue("password");
      if (current !== undefined) {
        isUpdatingFieldsRef.current = true;
        form.setFieldsValue({ password: undefined });
        form.setFields([{ name: "password", errors: [] }]);
        const frameId = requestAnimationFrame(() => {
          isUpdatingFieldsRef.current = false;
          validateForm();
        });
        return () => cancelAnimationFrame(frameId);
      }
    }
  }, [open, isEmailInputShown, loginType, form, validateForm]);

  // Handle contact method change for SHOP_CATALOG_EDITOR
  useEffect(() => {
    if (!open || isInitializingRef.current || !isEitherContactRole(selectedRole)) return;

    isUpdatingFieldsRef.current = true;
    if (contactMethod === "email") {
      form.setFieldsValue({ phoneNumber: undefined });
      form.setFields([{ name: "phoneNumber", errors: [] }]);
    } else if (contactMethod === "phone") {
      form.setFieldsValue({ email: undefined });
      form.setFields([{ name: "email", errors: [] }]);
    }

    const frameId = requestAnimationFrame(() => {
      isUpdatingFieldsRef.current = false;
      validateForm();
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [contactMethod, selectedRole, open, form, validateForm]);

  // Handle form value changes
  const handleValuesChange = useCallback(() => {
    // Skip validation if we're programmatically updating fields
    if (isUpdatingFieldsRef.current) {
      return;
    }
    debouncedValidate();
  }, [debouncedValidate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  const handleCancel = () => {
    form.resetFields();
    setIsFormValid(false);
    setSuccessInfo(null);
    setIsSubmitting(false);
    onCancel();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    try {
      const values = await form.validateFields();
      const payload: InviteEmployeeFormData = { ...values };

      // Convert the categories selection into the API shape: ["*"] when all
      // categories are selected, otherwise the concrete list of names.
      if (isShopStockManager(values.role)) {
        const cats = values.allowedCategories || [];
        payload.allowedCategories = cats.includes(ALL_CATEGORIES_VALUE)
          ? ["*"]
          : cats.filter((c) => c !== ALL_CATEGORIES_VALUE);
      }

      // Only carry loginType / password when the email login is actually in use.
      if (!isEmailInputShown) {
        delete payload.loginType;
        delete payload.password;
      } else if (payload.loginType !== "Email") {
        delete payload.password;
      }

      setIsSubmitting(true);
      const result = await onSubmit(payload);
      // When the parent returns account details (invite flow), keep the modal
      // open and show the confirmation. Otherwise (update / error) close as before.
      if (result) {
        setSuccessInfo(result);
        setIsSubmitting(false);
      } else {
        handleCancel();
      }
    } catch (error) {
      console.error("Validation failed:", error);
      setIsSubmitting(false);
    }
  };

  // Human-readable role labels for the success confirmation copy.
  const ROLE_LABELS: Record<string, string> = {
    SHOP_DELIVERY: "delivery agent",
    SHOP_SUPERVISOR: "supervisor",
    SHOP_PICKER: "picker",
    SHOP_CATALOG_EDITOR: "catalog editor",
    SHOP_STOCK_MANAGER: "stock manager",
  };

  const renderSuccessView = (info: InviteSuccessInfo) => {
    const { name, role, method, email, phoneNumber } = info;
    // In display mode the account already exists, so describe it in the present
    // tense; the invite flow announces that it "has been created".
    const isDisplay = !!displaySuccessInfo;

    if (method === "phone") {
      return (
        <>
          <p className="invite-success-line">
            {isDisplay ? (
              <>
                <strong>{name}</strong> is set up as a delivery agent.
              </>
            ) : (
              <>
                Account for <strong>{name}</strong> has been created as a
                delivery agent.
              </>
            )}
          </p>
          <p className="invite-success-line invite-success-next-step">
            <strong>Next step:</strong> {name} can now download{" "}
            <a
            href="https://play.google.com/store/apps/details?id=com.shopeg.crew"
            target="_blank"
            rel="noopener noreferrer"
            >
              <strong>&quot;ShopEG Crew&quot;</strong>
            </a> app from Play Store and
            login using <strong>{phoneNumber}</strong>.
          </p>
        </>
      );
    }

    const roleLabel = ROLE_LABELS[role] || "team member";
    const loginHint =
      method === "google" ? "with Google Sign in" : "using the password";

    return (
      <>
        <p className="invite-success-line">
          {isDisplay ? (
            <>
              <strong>{name}</strong> is set up as a {roleLabel}.
            </>
          ) : (
            <>
              Account for <strong>{name}</strong> has been created as a{" "}
              {roleLabel}.
            </>
          )}
        </p>
        <p className="invite-success-line invite-success-next-step">
          <strong>Next step:</strong> {name} can now login to{" "}
          <a
            href="https://shopeg-admin.web.app"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://shopeg-admin.web.app
          </a>{" "}
          with <strong>{email}</strong> and {loginHint}.
        </p>
      </>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      className="invite-employee-modal"
      closeIcon={<CloseOutlined className="invite-modal-close-icon" />}
      width={500}
    >
      <div className="invite-employee-modal-content">
        {successInfo ? (
          <div className="invite-employee-success">
            <div className="invite-success-icon">
              <CheckCircleFilled />
            </div>
            <h2 className="invite-employee-modal-title">
              {displaySuccessInfo ? "How to login" : "Account created"}
            </h2>
            <div className="invite-success-message">
              {renderSuccessView(successInfo)}
            </div>
            <div className="invite-employee-modal-actions">
              <ConfigProvider theme={{ token: { colorPrimary: "#4526a0" } }}>
                <Button
                  type="primary"
                  onClick={handleCancel}
                  className="invite-employee-submit-button"
                >
                  {displaySuccessInfo ? "Close" : "Done"}
                </Button>
              </ConfigProvider>
            </div>
          </div>
        ) : (
        <>
        <h2 className="invite-employee-modal-title">{title}</h2>
        <Form
          form={form}
          layout="vertical"
          className="invite-employee-form"
          onValuesChange={handleValuesChange}
        >
          {(isShopAdminRole || isShopSupervisorRole) && (
            <Form.Item
              label="Role"
              name="role"
              rules={roleRules}
              className="invite-form-item"
            >
              <Select
                allowClear
                placeholder="Select role"
                className="invite-form-select"
                disabled={!!initialData}
                options={[
                  { label: "Delivery Agent", value: "SHOP_DELIVERY" },
                  ...(isShopAdminRole
                    ? [{ label: "Supervisor", value: "SHOP_SUPERVISOR" }]
                    : []),
                  { label: "Picker", value: "SHOP_PICKER" },
                  { label: "Catalog Editor", value: "SHOP_CATALOG_EDITOR" },
                  { label: "Stock Manager", value: "SHOP_STOCK_MANAGER" },
                ]}
              />
            </Form.Item>
          )}
          <Form.Item
            label="Full Name"
            name="fullName"
            rules={fullNameRules}
            className="invite-form-item"
          >
            <Input
              placeholder="Enter employee name"
              className="invite-form-input"
            />
          </Form.Item>
          {/* Only show phone number for agents (SHOP_DELIVERY) */}
          {effectiveRole === "SHOP_DELIVERY" && (
            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={getPhoneNumberRules(effectiveRole)}
              className="invite-form-item"
            >
              <Input
                placeholder="Enter phone number"
                className="invite-form-input"
                prefix={<PhoneOutlined />}
                maxLength={11}
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={handlePhoneNumberChange}
              />
            </Form.Item>
          )}
          {/* Only show email for supervisors (SHOP_SUPERVISOR) and pickers */}
          {isEmailBasedRole(effectiveRole) && (
            <>
              <Form.Item
                label="Login Type"
                name="loginType"
                rules={[
                  { required: true, message: "Please select a login type" },
                ]}
                className="invite-form-item"
              >
                <Radio.Group className="login-type-radio-group">
                  <Radio value="Google">
                    <GoogleOutlined className="login-type-icon" /> Google
                  </Radio>
                  <Radio value="Email">
                    <MailOutlined className="login-type-icon" /> Email
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label="Email"
                name="email"
                rules={getEmailRules(effectiveRole)}
                className="invite-form-item"
              >
                <Input
                  type="email"
                  placeholder="Enter email address"
                  className="invite-form-input"
                  prefix={<MailOutlined />}
                  // Stock manager email is not editable on update (not sent in the
                  // update payload); it stays editable while inviting.
                  disabled={
                    !!initialData && isShopStockManager(effectiveRole || "")
                  }
                />
              </Form.Item>
              {isPasswordShown && (
                <Form.Item
                  label="Password"
                  name="password"
                  rules={getPasswordRules(true)}
                  className="invite-form-item"
                >
                  <Input.Password
                    placeholder="Enter password"
                    className="invite-form-input"
                    prefix={<LockOutlined />}
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
              )}
            </>
          )}
          {/* For Catalog Editor, show toggle between email and phone */}
          {isEitherContactRole(effectiveRole) && (
            <>
              <Form.Item
                label="Invite via"
                name="contactMethod"
                initialValue="phone"
                rules={[{ required: true, message: "Please select a contact method" }]}
                className="invite-form-item"
              >
                <Radio.Group>
                  <Radio value="phone">Phone Number</Radio>
                  <Radio value="email">Email</Radio>
                </Radio.Group>
              </Form.Item>
              {(contactMethod === "phone" || (!contactMethod && isEitherContactRole(effectiveRole))) && (
                <Form.Item
                  label="Phone Number"
                  name="phoneNumber"
                  rules={getPhoneNumberRules(contactMethod === "phone" ? ROLES.SHOP_DELIVERY : effectiveRole)}
                  className="invite-form-item"
                >
                  <Input
                    placeholder="Enter phone number"
                    className="invite-form-input"
                    prefix={<PhoneOutlined />}
                    maxLength={11}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={handlePhoneNumberChange}
                  />
                </Form.Item>
              )}
              {contactMethod === "email" && (
                <>
                  <Form.Item
                    label="Login Type"
                    name="loginType"
                    rules={[
                      { required: true, message: "Please select a login type" },
                    ]}
                    className="invite-form-item"
                  >
                    <Radio.Group className="login-type-radio-group">
                      <Radio value="Google">
                        <GoogleOutlined className="login-type-icon" /> Google
                      </Radio>
                      <Radio value="Email">
                        <MailOutlined className="login-type-icon" /> Email
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={getEmailRules(contactMethod === "email" ? ROLES.SHOP_SUPERVISOR : effectiveRole)}
                    className="invite-form-item"
                  >
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      className="invite-form-input"
                      prefix={<MailOutlined />}
                    />
                  </Form.Item>
                  {isPasswordShown && (
                    <Form.Item
                      label="Password"
                      name="password"
                      rules={getPasswordRules(true)}
                      className="invite-form-item"
                    >
                      <Input.Password
                        placeholder="Enter password"
                        className="invite-form-input"
                        prefix={<LockOutlined />}
                        iconRender={(visible) =>
                          visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                        }
                      />
                    </Form.Item>
                  )}
                </>
              )}
            </>
          )}
          {/* Allowed categories selector for Stock Managers */}
          {isShopStockManager(effectiveRole || "") && (
            <Form.Item
              label="Allowed Categories"
              name="allowedCategories"
              rules={[
                {
                  required: true,
                  message: "Please select at least one category",
                },
              ]}
              getValueFromEvent={normalizeCategories}
              className="invite-form-item"
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                loading={categoriesLoading}
                placeholder="Select allowed categories"
                className="invite-form-select"
                optionFilterProp="label"
                // Show each selected category as a removable pill. The selector
                // grows and wraps (see SCSS) so pills stack onto new rows;
                // tagRender hides the "All categories" sentinel so only real
                // categories render as pills (see renderCategoryTag).
                tagRender={renderCategoryTag}
                options={categoryOptions}
              />
            </Form.Item>
          )}
          <div className="invite-employee-modal-actions">
            <ConfigProvider theme={{ token: { colorPrimary: "#4526a0" } }}>
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
                loading={isSubmitting}
                className="invite-employee-submit-button"
              >
                {submitButtonText}
              </Button>
            </ConfigProvider>
            <Button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="invite-employee-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </Form>
        </>
        )}
      </div>
    </Modal>
  );
};

export default InviteEmployeeModal;
